import React, {PropsWithChildren, useEffect, useMemo, useState} from 'react';
import Container from 'react-bootstrap/Container';
import cx from 'classnames';
import Nav from 'react-bootstrap/Nav';
import Navbar from 'react-bootstrap/Navbar';
import {ChevronDown, List, XLg} from "react-bootstrap-icons";
import {Cookies} from "fearless-common-ui";
import {CloseButton} from "react-bootstrap";
import { routes } from "../../utils";
import styles from './Layout.module.scss'
import useAuth, {adminTokenKey} from "../hooks/useAuth";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import {Link, useLocation, useNavigate} from "react-router-dom";
import {concepts} from "fearless-common";
import SignUpButton from "../SignUpButton";
import ProfilePicture from "../ProfilePicture";
import logoSrc from './../../assets/images/fearless-league-logo-h100.png';
import Photo51 from "../../assets/images/logos/photo51-blue-desktop-icon.svg";
import SparkLogo from "../../assets/images/logos/spark-logo.svg";
import { externalLinks} from "../../utils/routes";
import { ACTIONS } from "../tracking/trackEvent";
import GetInTouch from "../GetInTouch";
import FooterLinks from "./FooterLinks";
import MenuSidebar from "./menu-sidebar/MenuSidebar";
import {isDev, isInAdminEnvironment, isInternal, isInUserEnvironment} from "../../utils/env";
import sideBarMenuItems from "./menu-sidebar/sidebarMenuItems";

type MenuLink = {
    href: string;
    label: string;
    description?: string;
    alt?: string;
    icon?: string;
    options?: MenuLink[]
}

const links: MenuLink[] = [
    {
        href: '',
        label: 'Software',
        options: [
            {
                href: routes.photo51,
                label: concepts.Photo51,
                description: 'Sequence editor for alignment, cloning, and more.',
                alt: 'Photo51 icon',
                icon: Photo51,
            },
        ].concat(
          isInternal() || isDev() ? [{
            href: routes.spark,
            label: 'Spark ELN + ED',
            description: 'The ELN that saves you time and automates your courses.',
            alt: 'Spark icon',
            icon: SparkLogo,
            }] : [],
        )
    },
    { href: routes.plans, label: concepts.Plans },
    { href: routes.faq, label: concepts.FAQ },
    { href: routes.resources, label: 'Resources' },
]

const productLinks: MenuLink[] = [
    { href: routes.photo51, label: concepts.Photo51 },
    { href: routes.plans, label: concepts.Plans },
];
const resourceLinks: MenuLink[] = [
    { href: routes.resources, label: 'Resources' },
    { href: routes.faq, label: concepts.FAQ },
];
const companyLinks: MenuLink[] = [
    { href: routes.aboutUs, label: 'About us' },
    { href: externalLinks.privacyPolicy, label: 'Privacy policy' },
    { href: externalLinks.cookiePolicy, label: 'Cookie Policy' },
    { href: externalLinks.termsOfService, label: 'Terms of Service' },
    { href: externalLinks.eula, label: 'EULA' },
]

const mobileLinkPadding = 'py-2 py-sm-auto';

/**
 * Render top menu navigation link
 */
const MobileLinkRender = ({ href, label, className = '', options }: { href: string; label: string | React.JSX.Element; className?: string; options?: MenuLink['options'] }) => {
    return (
      options ? (
        <Nav.Link
          as="div"
          className={styles.MainNavLinkDropdown}
        >
            <span className={`${styles.MainNavLinkDropdownLabel}`}>
              {label}
              &nbsp;
              <ChevronDown />
            </span>
            <div className={styles.MainSubMenuDropdown}>
                {options.map((o) => (
                  <Nav.Link
                    as="a"
                    key={o.href}
                    href={o.href}
                    className={`${styles.DropdownNavLink} d-flex align-items-center text-left w-100 ps-3`}
                  >
                      {o.icon && <img height={40} src={o.icon} alt={o.alt} className="me-3" />}
                      <div className="d-flex flex-column">
                          <strong>{o.label}</strong>
                          <small className="d-block gray-600" style={{ lineHeight: 1 }}>{o.description}</small>
                      </div>
                  </Nav.Link>
                ))}
            </div>
        </Nav.Link>
      ): (
        <Nav.Link
          as="a"
          href={href}
          className={`${styles.MainNavLink} text-center ${mobileLinkPadding} ${className}`}
        >
            {label}
        </Nav.Link>
      )
    )
};

function threshold(pathName: string) {
    if ([routes.photo51].indexOf(pathName) > -1) return 700
    return 200
}

const navBarId = 'basic-navbar-nav';

/**
 * Wraps main content in the general website layout. E.g. the top navigation bar,
 * website footer, etc.
 */
export default function Layout({ children }: PropsWithChildren) {
    const { user, reinstateAdminToken } = useAuth()
    const [expanded, setExpanded] = useState(false)
    const [userScrolledPastFold, setUserScrolledPastFold] = useState(false)
    const location = useLocation();
    const navigate = useNavigate();

    useEffect(() => {
        const onScroll = (e: Event) => {
            // @ts-ignore
            const newValue = e.target?.documentElement.scrollTop > threshold(location.pathname);
            if (userScrolledPastFold != newValue) {
                setUserScrolledPastFold(newValue);
            }
        };
        window.addEventListener("scroll", onScroll);

        return () => window.removeEventListener("scroll", onScroll);
    }, [userScrolledPastFold, location]);

    const inEmulation = useMemo(() => {
        return Boolean(Cookies.get(adminTokenKey));
    }, []);

    return (
        <div className={isInUserEnvironment() ? undefined : 'public'}>
            <Navbar
                expand="lg"
                className={cx({
                    [styles.Navbar]: true,
                    [styles.Expanded]: expanded,
                    [styles.Regular]: true,
                    'shadow': isInUserEnvironment() || isInAdminEnvironment(),
                })}
                fixed="top"
                onToggle={setExpanded}
                expanded={expanded}
                collapseOnSelect
            >
                <Container className="position-relative px-0 px-sm-3">
                    <a href={user ? routes.landing : routes.home} className="text-center">
                        <img
                            src={logoSrc}
                            alt="Fearless League logo"
                            height={26}
                            className={`${styles.Logo} mx-3 d-inline-block`}
                        />
                    </a>
                    <div className="d-inline-flex align-items-center gap-3">
                        {user && (
                          <Nav.Link as={Link} to={routes.landing} className="d-inline-flex align-items-center d-sm-none mt-2">
                            <ProfilePicture user={user} size="40px" />
                              <span>
                                <div>{user.first_name || user.last_name || user.email}</div>
                              </span>
                          </Nav.Link>
                        )}
                        <Navbar.Toggle
                            aria-controls={navBarId}
                            className={styles.NavbarTogglerIcon}
                        >
                            {expanded ? <XLg /> : <List />}
                        </Navbar.Toggle>
                    </div>
                    <Navbar.Collapse id={navBarId} className={`${styles.MobileMenu}`}>
                        <Nav className="me-auto">
                            {links.map((link) => <MobileLinkRender key={link.href} {...link} />)}
                        </Nav>
                        <Nav className="align-items-center">
                            {!user ? (
                              <>
                                <SignUpButton
                                    component="a"
                                    className="btn btn-lg btn-secondary ps-3"
                                    actionTag={ACTIONS.MAIN_NAVIGATION_TRY_FOR_FREE}
                                    text="Sign up"
                                />
                                <Nav.Link as="a" href={routes.login} className={mobileLinkPadding}>Log in</Nav.Link>
                              </>
                            ) : (
                                <>
                                    {sideBarMenuItems({ user }).map((value, index) => (
                                      <div key={index}>
                                        {value.separatorTop && <hr className="py-1 mb-1 border-white w-100"/>}
                                        <MobileLinkRender
                                          href={value.href}
                                          className={`${value.className} d-block d-sm-none ${value.href}`}
                                          label={value.label}
                                        />
                                          {value.options?.map((o, idx) => (
                                            <MobileLinkRender
                                              key={idx + 100}
                                              href={o.href}
                                              className={`${o.className} d-block d-sm-none`}
                                              label={o.label}
                                            />
                                          ))}
                                      </div>
                                    ))}
                                    <Nav.Link as={Link} to={routes.landing} className="d-none d-sm-inline-block">
                                        <div className="d-inline-flex align-items-center">
                                            <ProfilePicture user={user} size="50px" className="mt-2" />
                                            <div className="text-truncate">
                                                <div>{user.first_name || user.last_name || user.email}</div>
                                            </div>
                                        </div>
                                    </Nav.Link>
                                </>
                            )}
                        </Nav>
                    </Navbar.Collapse>
                    {expanded && (
                        <div className={`d-block d-sm-none animated fadeIn ${styles.Shade}`} onClick={() => setExpanded(false)}>
                            {/* This div blur everything behind it.*/}
                            &nbsp;
                        </div>
                    )}
                </Container>
            </Navbar>
            <main className={styles.Main}>
                {user && (isInUserEnvironment() || isInAdminEnvironment() || window.location.pathname.startsWith(routes.download)) ? <MenuSidebar>{children}</MenuSidebar> : <>{children}</>}
            </main>
            <footer>
                <Container className={`py-5 ${styles.Footer} mt-5`}>
                    <Row className="mb-5">
                        <Col xs={{ order: 1, span: 6 }} md={{ order: 1, span: true }} className="line-height-2">
                            <strong>Fearless League</strong>
                            <p className="line-height-2 mb-5 mb-md-0">
                                BioPartner, Room 3036 <br />
                                Galileiweg 8 <br />
                                2333 BD&nbsp;Leiden <br />
                                The Netherlands
                            </p>
                        </Col>
                        <Col xs={{ order: 3, span: 6 }} md={{ order: 2, span: true }}>
                            <strong className="text-secondary mb-2">Product</strong>
                            <FooterLinks links={productLinks} />
                        </Col>
                        <Col xs={{ order: 4, span: 6 }} md={{ order: 3, span: true }}>
                            <strong className="text-secondary mb-2">Resources</strong>
                            <FooterLinks links={resourceLinks} />
                        </Col>
                        <Col xs={{ order: 5, span: 6 }} md={{ order: 4, span: true }}>
                            <strong className="text-secondary mb-2">{concepts.CompanyName}</strong>
                            <FooterLinks links={companyLinks} />
                        </Col>
                        <Col xs={{ order: 1, span: 6 }} md={{ order: 5, span: true }}>
                            <p>Are you interested in our solutions or want to set up a collaboration?</p>
                            <GetInTouch />
                        </Col>
                    </Row>
                    <Row>
                        <Col className="gray-600 text-sm">
                            Copyright &copy; Fearless League {new Date().getFullYear()}.
                        </Col>
                    </Row>
                </Container>
            </footer>
            {inEmulation && (
              <div
                className="fixed-bottom max-width-500 rounded-top-right-lg opacity-75 end-0 text-bg-danger py-1 px-3 box-shadow-lg d-inline-flex align-items-center gap-2"
              >
                  <CloseButton onClick={() => reinstateAdminToken()} />
                  <small>Close emulated session as {user?.first_name} and continue as admin</small>
              </div>
            )}
        </div>
    );
}
